import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, LogOut } from "lucide-react";
import { api, imgUrl, LOGO_WHITE } from "../lib/api";

const TABS = ["ARTWORK", "PRINTS", "EVENTS", "INQUIRIES", "SUBSCRIBERS", "SETTINGS"];
const CATEGORIES = ["pop-culture", "portraits", "street", "originals"];

const inputCls = "w-full h-11 bg-ink border border-white/15 px-3 text-sm outline-none focus:border-pink";
const labelCls = "block text-[10px] font-bold tracking-[0.2em] text-smoke mb-1.5";

const F = ({ label, children }) => (
  <div><span className={labelCls}>{label}</span>{children}</div>
);

const emptyArt = { title: "", category: "pop-culture", year: "", medium: "", dimensions: "", original_available: "", print_available: "", original_price: "", print_price: "", description: "", featured: false, hero: false, sort_order: 99 };

function ArtworkPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyArt);
  const [image, setImage] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/artworks").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries({ ...form, featured: String(!!form.featured), hero: String(!!form.hero) }).forEach(([k, v]) => fd.append(k, v ?? ""));
    if (image) fd.append("image", image);
    try {
      if (editing) await api.put(`/admin/artworks/${editing}`, fd);
      else await api.post("/admin/artworks", fd);
      toast.success("Saved");
      setForm(emptyArt); setImage(null); setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const edit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title || "", category: a.category || "pop-culture", year: a.year || "", medium: a.medium || "",
      dimensions: a.dimensions || "",
      original_available: a.original_available == null ? "" : String(a.original_available),
      print_available: a.print_available == null ? "" : String(a.print_available),
      original_price: a.original_price ?? "", print_price: a.print_price ?? "",
      description: a.description || "", featured: !!a.featured, hero: !!a.hero, sort_order: a.sort_order ?? 99,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="admin-artwork-panel">
      <form onSubmit={save} className="border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="artwork-form">
        <F label="TITLE (optional)"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="artwork-title" /></F>
        <F label="CATEGORY">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="artwork-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </F>
        <F label="YEAR"><input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inputCls} data-testid="artwork-year" /></F>
        <F label="MEDIUM"><input value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className={inputCls} data-testid="artwork-medium" /></F>
        <F label="DIMENSIONS"><input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className={inputCls} data-testid="artwork-dimensions" /></F>
        <F label="SORT ORDER"><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputCls} data-testid="artwork-sort" /></F>
        <F label="ORIGINAL AVAILABLE">
          <select value={form.original_available} onChange={(e) => setForm({ ...form, original_available: e.target.value })} className={inputCls} data-testid="artwork-original-available">
            <option value="">HIDE</option><option value="true">YES</option><option value="false">NO</option>
          </select>
        </F>
        <F label="PRINT AVAILABLE">
          <select value={form.print_available} onChange={(e) => setForm({ ...form, print_available: e.target.value })} className={inputCls} data-testid="artwork-print-available">
            <option value="">HIDE</option><option value="true">YES</option><option value="false">NO</option>
          </select>
        </F>
        <F label="ORIGINAL PRICE ($)"><input type="number" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className={inputCls} data-testid="artwork-original-price" /></F>
        <F label="PRINT PRICE ($)"><input type="number" step="0.01" value={form.print_price} onChange={(e) => setForm({ ...form, print_price: e.target.value })} className={inputCls} data-testid="artwork-print-price" /></F>
        <div className="sm:col-span-2"><F label="DESCRIPTION"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-ink border border-white/15 px-3 py-2 text-sm outline-none focus:border-pink" data-testid="artwork-description" /></F></div>
        <F label={editing ? "REPLACE IMAGE (optional)" : "ARTWORK IMAGE *"}>
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="text-xs text-smoke" data-testid="artwork-image" />
        </F>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-xs font-bold tracking-widest min-h-[44px]"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="artwork-featured" /> FEATURED</label>
          <label className="flex items-center gap-2 text-xs font-bold tracking-widest min-h-[44px]"><input type="checkbox" checked={form.hero} onChange={(e) => setForm({ ...form, hero: e.target.checked })} data-testid="artwork-hero" /> HERO</label>
        </div>
        <div className="sm:col-span-2 flex gap-3">
          <button type="submit" className="h-11 px-8 bg-bone text-ink text-xs font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200" data-testid="artwork-save">
            {editing ? "UPDATE ARTWORK" : "ADD ARTWORK"}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyArt); setImage(null); }} className="h-11 px-6 border border-white/20 text-xs font-bold tracking-widest" data-testid="artwork-cancel">CANCEL</button>}
        </div>
      </form>

      <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
        {items.map((a) => (
          <div key={a.id} className="py-3 flex items-center gap-4" data-testid={`artwork-row-${a.id}`}>
            {a.images?.sm && <img src={imgUrl(a.images.sm)} alt="" className="w-12 h-12 object-cover border border-white/10" />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg leading-none truncate">{a.title || "UNTITLED"} <span className="text-smoke text-xs">· {a.slug}</span></p>
              <p className="text-[10px] tracking-widest text-smoke mt-1">{(a.category || "").toUpperCase()}{a.featured ? " · FEATURED" : ""}{a.hero ? " · HERO" : ""}</p>
            </div>
            <button onClick={() => edit(a)} className="h-9 px-4 border border-white/20 text-[10px] font-bold tracking-widest hover:border-pink hover:text-pink transition-colors duration-200" data-testid={`artwork-edit-${a.id}`}>EDIT</button>
            <button onClick={async () => { await api.delete(`/admin/artworks/${a.id}`); load(); }} className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink" aria-label="Delete" data-testid={`artwork-delete-${a.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyPrint = { title: "", artwork_id: "", price: "", dimensions: "", edition_size: "", edition_remaining: "", paper: "", description: "", type: "limited", active: true, sort_order: 99 };

function PrintsPanel({ artworks }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyPrint);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/prints").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      price: form.price === "" ? null : Number(form.price),
      edition_size: form.edition_size === "" ? null : Number(form.edition_size),
      edition_remaining: form.edition_remaining === "" ? null : Number(form.edition_remaining),
      sort_order: Number(form.sort_order) || 99,
    };
    try {
      if (editing) await api.put(`/admin/prints/${editing}`, body);
      else await api.post("/admin/prints", body);
      toast.success("Saved");
      setForm(emptyPrint); setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-prints-panel">
      <form onSubmit={save} className="border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="print-form">
        <F label="LINKED ARTWORK (pulls image)">
          <select value={form.artwork_id} onChange={(e) => setForm({ ...form, artwork_id: e.target.value })} className={inputCls} data-testid="print-artwork">
            <option value="">NONE</option>
            {artworks.map((a) => <option key={a.id} value={a.id}>{a.title || a.slug}</option>)}
          </select>
        </F>
        <F label="PRODUCT TYPE">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="print-type">
            <option value="limited">SIGNED + NUMBERED LIMITED</option>
            <option value="open">OPEN PRINT</option>
            <option value="digital">DIGITAL DOWNLOAD</option>
          </select>
        </F>
        <F label="TITLE (optional)"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="print-title-input" /></F>
        <F label="PRICE ($)"><input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} data-testid="print-price-input" /></F>
        <F label="PRINT DIMENSIONS"><input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className={inputCls} data-testid="print-dimensions-input" /></F>
        <F label="EDITION SIZE"><input type="number" value={form.edition_size} onChange={(e) => setForm({ ...form, edition_size: e.target.value })} className={inputCls} data-testid="print-edition-size" /></F>
        <F label="QTY REMAINING (0 = SOLD OUT)"><input type="number" value={form.edition_remaining} onChange={(e) => setForm({ ...form, edition_remaining: e.target.value })} className={inputCls} data-testid="print-edition-remaining" /></F>
        <F label="PAPER / MATERIAL"><input value={form.paper} onChange={(e) => setForm({ ...form, paper: e.target.value })} className={inputCls} data-testid="print-paper-input" /></F>
        <F label="SORT ORDER"><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputCls} data-testid="print-sort" /></F>
        <div className="sm:col-span-2"><F label="DESCRIPTION"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-ink border border-white/15 px-3 py-2 text-sm outline-none focus:border-pink" data-testid="print-description-input" /></F></div>
        <label className="flex items-center gap-2 text-xs font-bold tracking-widest min-h-[44px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="print-active" /> LIVE IN SHOP</label>
        <div className="sm:col-span-2 flex gap-3">
          <button type="submit" className="h-11 px-8 bg-bone text-ink text-xs font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200" data-testid="print-save">
            {editing ? "UPDATE PRINT" : "ADD PRINT"}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyPrint); }} className="h-11 px-6 border border-white/20 text-xs font-bold tracking-widest" data-testid="print-cancel">CANCEL</button>}
        </div>
      </form>

      <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
        {items.map((p) => (
          <div key={p.id} className="py-3 flex items-center gap-4" data-testid={`print-row-${p.id}`}>
            {p.images?.sm && <img src={imgUrl(p.images.sm)} alt="" className="w-12 h-12 object-cover border border-white/10" />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg leading-none truncate">{p.title || "UNTITLED"}</p>
              <p className="text-[10px] tracking-widest text-smoke mt-1">
                {(p.type || "limited").toUpperCase()} · {p.price != null ? `$${p.price}` : "NO PRICE"}{p.edition_size ? ` · EDITION OF ${p.edition_size}` : ""}{p.edition_remaining === 0 ? " · SOLD OUT" : ""}{p.active ? "" : " · HIDDEN"}
              </p>
            </div>
            <button onClick={() => { setEditing(p.id); setForm({ ...emptyPrint, ...p, price: p.price ?? "", edition_size: p.edition_size ?? "", edition_remaining: p.edition_remaining ?? "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="h-9 px-4 border border-white/20 text-[10px] font-bold tracking-widest hover:border-pink hover:text-pink transition-colors duration-200" data-testid={`print-edit-${p.id}`}>EDIT</button>
            <button onClick={async () => { await api.delete(`/admin/prints/${p.id}`); load(); }} className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink" aria-label="Delete" data-testid={`print-delete-${p.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyEvent = { name: "", date: "", time: "", location: "", phone: "", link: "", description: "", recurrence: "" };

function EventsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/events").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/events/${editing}`, form);
      else await api.post("/admin/events", form);
      toast.success("Saved");
      setForm(emptyEvent); setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-events-panel">
      <form onSubmit={save} className="border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="event-form">
        <F label="EVENT NAME *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="event-name" /></F>
        <F label="RECURRENCE">
          <select value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className={inputCls} data-testid="event-recurrence">
            <option value="">ONE-TIME</option>
            <option value="first-friday">FIRST FRIDAY (EVERY MONTH)</option>
          </select>
        </F>
        <F label="DATE (leave blank if recurring / TBA)"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} data-testid="event-date" /></F>
        <F label="TIME"><input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} data-testid="event-time" /></F>
        <F label="LOCATION"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} data-testid="event-location" /></F>
        <F label="PHONE"><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="event-phone" /></F>
        <div className="sm:col-span-2"><F label="SHORT DESCRIPTION"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-ink border border-white/15 px-3 py-2 text-sm outline-none focus:border-pink" data-testid="event-description" /></F></div>
        <F label="INFO / DIRECTIONS URL"><input type="url" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} data-testid="event-link" /></F>
        <div className="flex items-end gap-3">
          <button type="submit" className="h-11 px-8 bg-bone text-ink text-xs font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200" data-testid="event-save">
            {editing ? "UPDATE EVENT" : "ADD EVENT"}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(emptyEvent); }} className="h-11 px-6 border border-white/20 text-xs font-bold tracking-widest" data-testid="event-cancel">CANCEL</button>}
        </div>
      </form>

      <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
        {items.map((ev) => (
          <div key={ev.id} className="py-3 flex items-center gap-4" data-testid={`event-row-${ev.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg leading-none truncate">{ev.name}</p>
              <p className="text-[10px] tracking-widest text-smoke mt-1">{ev.recurrence === "first-friday" ? "EVERY FIRST FRIDAY" : ev.date || "DATE TBA"}{ev.location ? ` · ${ev.location}` : ""}</p>
            </div>
            <button onClick={() => { setEditing(ev.id); setForm({ name: ev.name, date: ev.date || "", time: ev.time || "", location: ev.location || "", phone: ev.phone || "", link: ev.link || "", description: ev.description || "", recurrence: ev.recurrence || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="h-9 px-4 border border-white/20 text-[10px] font-bold tracking-widest hover:border-pink hover:text-pink transition-colors duration-200" data-testid={`event-edit-${ev.id}`}>EDIT</button>
            <button onClick={async () => { await api.delete(`/admin/events/${ev.id}`); load(); }} className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink" aria-label="Delete" data-testid={`event-delete-${ev.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-inquiries-panel">
      {items.length === 0 && <p className="text-sm text-smoke">No inquiries yet.</p>}
      {items.map((q) => (
        <div key={q.id} className="border border-white/10 p-4" data-testid={`inquiry-${q.id}`}>
          <div className="flex items-center justify-between gap-3">
            <span className={`text-[9px] font-extrabold tracking-[0.25em] px-2 py-1 border ${q.kind === "mural" ? "border-acid text-acid" : "border-pink text-pink"}`}>
              {q.kind === "mural" ? "MURAL" : q.inquiry_type || "CONTACT"}
            </span>
            <button onClick={async () => { await api.delete(`/admin/inquiries/${q.id}`); load(); }} className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink" aria-label="Delete" data-testid={`inquiry-delete-${q.id}`}><Trash2 size={15} /></button>
          </div>
          <p className="mt-3 font-bold text-sm">{q.name} — <a href={`mailto:${q.email}`} className="text-pink">{q.email}</a>{q.phone ? ` · ${q.phone}` : ""}</p>
          <p className="text-[10px] text-smoke mt-1">{new Date(q.created_at).toLocaleString()}</p>
          {q.kind === "mural" ? (
            <div className="mt-3 text-xs text-bone/80 space-y-1">
              <p>{q.project_type} · {q.indoor_outdoor || "—"} · {q.project_location || "—"}</p>
              <p>Wall: {q.wall_dimensions || "—"} · Budget: {q.budget || "—"} · Target: {q.completion_date || "—"}</p>
              {q.style_concept && <p className="mt-2 whitespace-pre-wrap">{q.style_concept}</p>}
              {q.photo_url && <a href={imgUrl(q.photo_url)} target="_blank" rel="noopener noreferrer" className="text-pink underline underline-offset-4 inline-block mt-1" data-testid={`inquiry-photo-${q.id}`}>View wall photo</a>}
            </div>
          ) : (
            <p className="mt-3 text-xs text-bone/80 whitespace-pre-wrap">{q.message}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function SubscribersPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/subscribers").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-subscribers-panel">
      <p className="text-sm text-smoke mb-4" data-testid="subscribers-count">{items.length} subscriber{items.length === 1 ? "" : "s"} on the mailing list.</p>
      <div className="divide-y divide-white/10 border-y border-white/10">
        {items.map((s) => (
          <div key={s.id} className="py-3 flex items-center gap-4" data-testid={`subscriber-${s.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{s.email}</p>
              <p className="text-[10px] tracking-widest text-smoke mt-0.5">{new Date(s.created_at).toLocaleDateString()}</p>
            </div>
            <button onClick={async () => { await api.delete(`/admin/subscribers/${s.id}`); load(); }} className="w-9 h-9 flex items-center justify-center text-smoke hover:text-pink" aria-label="Remove" data-testid={`subscriber-delete-${s.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="py-6 text-sm text-smoke">No subscribers yet — the signup form lives in the site footer.</p>}
      </div>
    </div>
  );
}

function SettingsPanel({ settings, reload }) {
  const [form, setForm] = useState(settings || { instagram: "", about_short: "", about_long: "" });
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      reload();
    } catch { toast.error("Save failed"); }
  };

  return (
    <form onSubmit={save} className="border border-white/10 p-5 space-y-4" data-testid="settings-form">
      <F label="INSTAGRAM URL"><input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={inputCls} data-testid="settings-instagram" /></F>
      <F label="SHOPIFY STORE URL (merch tiles link here when set)"><input type="url" value={form.shopify_url || ""} onChange={(e) => setForm({ ...form, shopify_url: e.target.value })} className={inputCls} data-testid="settings-shopify" /></F>
      <F label="ABOUT — SHORT INTRO (home page)"><textarea rows={2} value={form.about_short} onChange={(e) => setForm({ ...form, about_short: e.target.value })} className="w-full bg-ink border border-white/15 px-3 py-2 text-sm outline-none focus:border-pink" data-testid="settings-about-short" /></F>
      <F label="ABOUT — FULL BIO (about page, blank line between paragraphs)"><textarea rows={6} value={form.about_long} onChange={(e) => setForm({ ...form, about_long: e.target.value })} className="w-full bg-ink border border-white/15 px-3 py-2 text-sm outline-none focus:border-pink" data-testid="settings-about-long" /></F>
      <button type="submit" className="h-11 px-8 bg-bone text-ink text-xs font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200" data-testid="settings-save">SAVE SETTINGS</button>
    </form>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("ARTWORK");
  const [artworks, setArtworks] = useState([]);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  const loadShared = useCallback(() => {
    api.get("/admin/artworks").then((r) => setArtworks(r.data)).catch(() => {});
    api.get("/config").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(() => { setAuthed(true); loadShared(); })
      .catch(() => { setAuthed(false); navigate("/admin/login"); });
  }, [navigate, loadShared]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("akdp-token");
    navigate("/admin/login");
  };

  if (!authed) return <div className="min-h-screen bg-ink flex items-center justify-center text-smoke text-sm">Checking access…</div>;

  return (
    <div className="min-h-screen bg-ink text-bone px-4 sm:px-8 py-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <img src={LOGO_WHITE} alt="ARTILLERY KDP" className="h-7 w-auto" draggable={false} />
          <p className="text-[10px] tracking-[0.3em] text-smoke mt-2">CONTENT MANAGEMENT</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-xs font-bold tracking-widest text-smoke hover:text-pink transition-colors duration-200 min-h-[44px]" data-testid="admin-logout">
          <LogOut size={15} /> LOG OUT
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`shrink-0 min-h-[44px] px-4 text-[11px] font-extrabold tracking-[0.15em] border transition-colors duration-200 ${tab === t ? "bg-bone text-ink border-bone" : "border-white/20 text-bone/70"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "ARTWORK" && <ArtworkPanel />}
      {tab === "PRINTS" && <PrintsPanel artworks={artworks} />}
      {tab === "EVENTS" && <EventsPanel />}
      {tab === "INQUIRIES" && <InquiriesPanel />}
      {tab === "SUBSCRIBERS" && <SubscribersPanel />}
      {tab === "SETTINGS" && <SettingsPanel settings={settings} reload={loadShared} />}
    </div>
  );
}
